import { useIntl } from "react-intl";
import BedSolid from "src/design-system/icons/BedSolid.svg?react";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { useScroll } from "src/ScrollContext";
import { useNavigateToHotelsPage } from "src/utils/hooks/useNavigateToHotelsPage";
import useSearch from "src/utils/hooks/useSearch";
import { type GeoKind, isPointKind } from "src/utils/geoKind";
import { useRef } from "react";
import type { HotelProviderKind } from "src/utils/types/accommodationProviders";
import type { AccomPromoLoggingCategory } from "src/analytics/analyticsEventTypes";
import { useIsTripScreen } from "src/utils/hooks/useIsTripScreen";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { useAnalyticsEventHasEnteredViewport } from "src/utils/hooks/useAnalyticsEventHasEnteredViewport";
import { SegmentPromoDriveVariant } from "../SegmentPromoDriveVariant/SegmentPromoDriveVariant";
import messages from "./SegmentAccommodationPromoDriveVariant.messages";

type Props = {
  location: string;
  kind: GeoKind;
  loggingFrom: AccomPromoLoggingCategory;
  hotelProvider: HotelProviderKind;
  id: string;
};

export function SegmentAccommodationPromoDriveVariant({
  location,
  kind,
  loggingFrom,
  hotelProvider,
  id,
}: Props) {
  const intl = useIntl();
  const { setScrollTop } = useScroll();
  const { navigateToHotels } = useNavigateToHotelsPage();
  const { origin, destination } = useSearch();
  const { isMdScreen } = useScreenMinWidth();
  const isFullSizePromo = !isMdScreen;
  const isTripsScreen = useIsTripScreen();
  const copyPreposition = isPointKind(kind) ? "Near" : "Around";
  const calloutMessage = messages[`findHotels${copyPreposition}`];

  // We want to see when an accommodation promo gets into view but only fire the log once.
  const accommodationPromoRef = useRef<HTMLButtonElement>(null);
  useAnalyticsEventHasEnteredViewport({
    ref: accommodationPromoRef,
    category: "Screen",
    action: "AccomPromoVisible",
    label: `ap0:${hotelProvider}`,
  });

  function handleClick() {
    const gaLoggingCategory =
      loggingFrom === "Schedule" ? "ScheduleDetails" : loggingFrom;

    sendAnalyticsInteractionEvent({
      category: gaLoggingCategory,
      action: "Click:HotelPromo",
    });
    setScrollTop(0);
    if (isTripsScreen) {
      sendAnalyticsInteractionEvent({
        category: "TripPlanner",
        action: "Click:HotelPromo",
      });
      navigateToHotels({
        originCanonical: origin?.canonicalName,
        destinationCanonical: destination?.canonicalName,
      });
    } else {
      navigateToHotels();
    }
  }

  return (
    <SegmentPromoDriveVariant
      ref={accommodationPromoRef}
      heading={intl.formatMessage(calloutMessage, {
        location,
      })}
      tagline={intl.formatMessage(messages.tagline)}
      icon={<BedSolid className="text-icon-primary" />}
      iconAlign={isFullSizePromo ? "left" : "right"}
      outline={isFullSizePromo}
      fullWidth={isFullSizePromo}
      onClick={handleClick}
    />
  );
}
